import { Anchor, Box, Button, Grid, Group, List, Stack, Table, Text } from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import { useDisclosure } from "@mantine/hooks";
import { ArrowLeftIcon, CheckCircleIcon, CheckSquareIcon, SpeakerSlashIcon } from "@phosphor-icons/react";
import { DisabledReason, HelpButtonGroup, useHelpContext } from "@dehub/theme";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnApi, ReturnPreventionAlertApi } from "../../../apis";
import { DeliveryActionButton } from "../../../components/delivery/action-controls";
import { TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { ReturnStatusBadge, reasonLabel } from "../../../components/return/atoms";
import {
  ALERT_HELP,
  AcknowledgeAlertModal,
  MuteAlertModal,
  ResolveAlertModal,
  useAlertActions,
} from "../../../components/return/alert-action-modals";
import { AlertSeverityBadge } from "../../../components/return/alert-severity-badge";
import { AlertStatusBadge } from "../../../components/return/alert-status-badge";
import { triggerLabel } from "../../../components/return/alert-trigger-label";
import { percentText } from "../../../components/return/prevention-alert-table";
import { DetailCard, SideRow, Field } from "../../../components/return/detail-card";
import { DeniedCard, ListState } from "../../../components/ui";
import { currencyFormat } from "../../../utils/currency";
import { dateFormat, dateTimeSecFormat } from "../../../utils/date";

/** @dehub/help key namespace of this screen (spec 12 §0); action/reason/confirm rows are shared with the list. */
const HELP = "order.returns-prevention-detail";

// Donut slices: tenant primary first, then dark and grey steps (no extra hues).
const DONUT_COLORS = [
  "var(--mantine-primary-color-filled)",
  "var(--mantine-color-dark-8)",
  "var(--mantine-color-gray-5)",
  "var(--mantine-color-gray-7)",
  "var(--mantine-color-gray-3)",
  "var(--mantine-color-dark-4)",
];

export function PreventionDetailPage() {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const { stateOf, hasAccess } = useAlertActions();

  const { data, error, isLoading, mutate } = useSWR(
    id && hasAccess ? ["prevention.get", id] : null,
    () => ReturnPreventionAlertApi.get(id),
    { revalidateOnFocus: false },
  );

  const [ackOpened, { open: openAck, close: closeAck }]              = useDisclosure(false);
  const [muteOpened, { open: openMute, close: closeMute }]           = useDisclosure(false);
  const [resolveOpened, { open: openResolve, close: closeResolve }]  = useDisclosure(false);

  const alert    = data?.alert;
  const metric   = data?.metric ?? null;

  // @dehub/help: the alert status drives the drawer's «Одоо» step (R15 flow) and the status tab.
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ status: alert?.status ?? undefined });
  }, [alert?.status, setHelpContext]);

  // §27 reason-breakdown donut — reason → count from the product metric snapshot.
  const reasonEntries = metric
    ? Object.entries(metric.reasonBreakdown ?? {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])
    : [];
  const reasonDonut = reasonEntries.map(([reason, count], i) => ({
    name : reasonLabel(reason),
    value: count,
    color: DONUT_COLORS[i % DONUT_COLORS.length]
  }));
  const totalReasonCount = reasonEntries.reduce((s, [, n]) => s + n, 0);

  // Linked recent returns for the alert's product. The returns list endpoint has
  // no productId filter, so we search by SKU (or product name) — fuzzy but the
  // best available match.
  const productSearch = alert?.sku ?? alert?.productName ?? null;
  const { data: recent } = useSWR(
    productSearch ? ["prevention.recent-returns", productSearch] : null,
    () => ReturnApi.list({ search: productSearch as string, limit: 5, sortBy: "requestedAt", sortDir: "desc" }),
    { revalidateOnFocus: false },
  );
  const recentRows = recent?.returns ?? [];

  const breadcrumb = [
    { to: "/",                    label: t("breadcrumb.home") },
    { to: "/returns",             label: t("breadcrumb.returns") },
    { to: "/returns/prevention",  label: t("breadcrumb.prevention") },
    { label: alert?.title ?? t("breadcrumb.detail") },
  ];

  if (!hasAccess) {
    return (
      <PageLayout title={t("prevention.detail.title")} breadcrumb={breadcrumb}>
        <DeniedCard permission="RETURN_ANALYTICS_VIEW" />
      </PageLayout>
    );
  }

  const ack = alert ? stateOf("acknowledge", alert.status) : { enabled: false, reason: null, code: null };
  const mute = alert ? stateOf("mute", alert.status) : { enabled: false, reason: null, code: null };
  const resolve = alert ? stateOf("resolve", alert.status) : { enabled: false, reason: null, code: null };
  const notFound = (error as { statusCode?: number } | undefined)?.statusCode === 404;

  // Actions live in the page header (canvas Ord_PreventionDetail); unavailable ones stay, aria-disabled.
  // @dehub/help: attached «?» (R2) + reason popover while unavailable (R4).
  const extra = alert
    ? [
      <DisabledReason key="ack" helpKey={`${ALERT_HELP}.reason.alert-action`} reason={ack.code} when={!ack.enabled} position="bottom-end">
        <HelpButtonGroup helpKey={`${ALERT_HELP}.action.acknowledge`}>
          <DeliveryActionButton data-help-id="alert-acknowledge" state={{ allowed: ack.enabled, reason: ack.reason }} variant="default" leftSection={<CheckCircleIcon size={18} />} onClick={openAck}>
            {t("prevention.detail.acknowledge")}
          </DeliveryActionButton>
        </HelpButtonGroup>
      </DisabledReason>,
      <DisabledReason key="mute" helpKey={`${ALERT_HELP}.reason.alert-action`} reason={mute.code} when={!mute.enabled} position="bottom-end">
        <HelpButtonGroup helpKey={`${ALERT_HELP}.action.mute`}>
          <DeliveryActionButton data-help-id="alert-mute" state={{ allowed: mute.enabled, reason: mute.reason }} variant="default" leftSection={<SpeakerSlashIcon size={18} />} onClick={openMute}>
            {t("prevention.detail.mute")}
          </DeliveryActionButton>
        </HelpButtonGroup>
      </DisabledReason>,
      <DisabledReason key="resolve" helpKey={`${ALERT_HELP}.reason.alert-action`} reason={resolve.code} when={!resolve.enabled} position="bottom-end">
        <HelpButtonGroup helpKey={`${ALERT_HELP}.action.resolve`}>
          <DeliveryActionButton data-help-id="alert-resolve" state={{ allowed: resolve.enabled, reason: resolve.reason }} leftSection={<CheckSquareIcon size={18} />} onClick={openResolve}>
            {t("prevention.detail.resolve")}
          </DeliveryActionButton>
        </HelpButtonGroup>
      </DisabledReason>,
    ]
    : undefined;

  return (
    <PageLayout
      title={alert?.title ?? t("prevention.detail.title")}
      subTitle={t("prevention.detail.subtitle")}
      breadcrumb={breadcrumb}
      loading={isLoading && !data}
      extra={extra}
    >
      {error && !data ? (
        <Stack gap="md" align="flex-start">
          <ListState
            state="error"
            title={notFound ? t("prevention.detail.notFound") : undefined}
            description={notFound ? t("prevention.detail.notFoundText") : undefined}
            onRetry={notFound ? undefined : () => mutate()}
            errorCode={(error as { statusCode?: number })?.statusCode}
          />
          <Button variant="default" leftSection={<ArrowLeftIcon size={18} />} onClick={() => navigate("/returns/prevention")}>
            {t("prevention.detail.backToList")}
          </Button>
        </Stack>
      ) : null}

      {alert && (
        <Grid>
          {/* ── Left: summary, product metric, linked returns ── */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              <DetailCard>
                <Stack gap="sm">
                  <Group gap="sm" wrap="wrap">
                    <AlertSeverityBadge severity={alert.severity} />
                    <AlertStatusBadge status={alert.status} help />
                  </Group>
                  <Text size="sm">{alert.message}</Text>
                  <Group gap="xl" wrap="wrap">
                    <Field label={t("prevention.detail.createdAt")} value={dateFormat(alert.createdAt)} mono />
                    {alert.acknowledgedAt && (
                      <Field label={t("prevention.detail.acknowledgedAt")} value={dateTimeSecFormat(alert.acknowledgedAt)} mono />
                    )}
                    {alert.mutedUntil && (
                      <Field label={t("prevention.detail.mutedUntil")} value={dateTimeSecFormat(alert.mutedUntil)} mono />
                    )}
                    {alert.resolvedAt && (
                      <Field label={t("prevention.detail.resolvedAt")} value={dateTimeSecFormat(alert.resolvedAt)} mono />
                    )}
                  </Group>
                  {alert.status === "RESOLVED" ? (
                    <Field label={t("prevention.detail.resolutionNotes")} value={alert.resolutionNotes} />
                  ) : null}
                  {alert.status === "MUTED" ? (
                    <Text size="sm" c="var(--nw-text-2)">{t("prevention.detail.mutedNote")}</Text>
                  ) : null}
                </Stack>
              </DetailCard>

              {/* Product metric snapshot + reason breakdown (§27) */}
              {metric && (
                <DetailCard title={t("prevention.detail.productMetric")} helpKey={`${HELP}.section.product-metric`}>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Box>
                        <SideRow label={t("prevention.detail.soldQty")} value={metric.totalSoldQuantity.toLocaleString("mn-MN")} mono />
                        <SideRow label={t("prevention.detail.returnedQty")} value={metric.totalReturnedQuantity.toLocaleString("mn-MN")} mono />
                        <SideRow label={t("prevention.detail.returnRate")} value={percentText(metric.returnRatePercent)} mono />
                        {metric.previousReturnRatePercent != null && (
                          <SideRow label={t("prevention.detail.previousRate")} value={percentText(metric.previousReturnRatePercent)} mono />
                        )}
                        {metric.topReason && (
                          <SideRow
                            label={t("prevention.detail.topReason")}
                            // BE stores the share as a percent (50.00 = 50%).
                            value={`${reasonLabel(metric.topReason)}${metric.topReasonShare != null ? ` (${metric.topReasonShare.toFixed(0)}%)` : ""}`}
                          />
                        )}
                        <SideRow label={t("prevention.detail.damagedWriteoff")} value={`${metric.damagedQuantity} / ${metric.writeOffQuantity}`} mono />
                      </Box>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Stack gap="xs" align="center" justify="center">
                        <Text size="sm" c="var(--nw-text-2)">{t("prevention.detail.reasonBreakdown")}</Text>
                        {reasonDonut.length > 0 ? (
                          <>
                            <DonutChart
                              data={reasonDonut}
                              size={170}
                              thickness={28}
                              withTooltip
                              chartLabel={`${totalReasonCount}`}
                            />
                            <Stack gap={4} w="100%">
                              {reasonDonut.map((d) => (
                                <Group key={d.name} justify="space-between" wrap="nowrap">
                                  <Group gap="xs" wrap="nowrap">
                                    <Box component="span" w={10} h={10} bg={d.color} style={{ borderRadius: 2, flexShrink: 0 }} aria-hidden />
                                    <Text size="sm">{d.name}</Text>
                                  </Group>
                                  <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{d.value}</Text>
                                </Group>
                              ))}
                            </Stack>
                          </>
                        ) : (
                          <Text size="sm" c="var(--nw-text-2)">{t("prevention.detail.noData")}</Text>
                        )}
                      </Stack>
                    </Grid.Col>
                  </Grid>
                </DetailCard>
              )}

              {/* Linked recent returns (by SKU/product search) */}
              {productSearch && recentRows.length > 0 && (
                <DetailCard title={t("prevention.detail.linkedRecent")} helpKey={`${HELP}.section.linked-recent`}>
                  <TableFrame>
                    <Table.ScrollContainer minWidth={560}>
                      <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>{t("listTable.col.retNumber")}</Table.Th>
                            <Table.Th>{t("filters.reasonLabel")}</Table.Th>
                            <Table.Th>{t("detail.field.date")}</Table.Th>
                            <Table.Th ta="right">{t("listTable.col.amount")}</Table.Th>
                            <Table.Th>{t("listTable.col.status")}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {recentRows.map((r) => (
                            <Table.Tr key={r.returnRequestId}>
                              <Table.Td>
                                <Anchor component={Link} to={`/returns/${r.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no">
                                  {r.returnNumber}
                                </Anchor>
                              </Table.Td>
                              <Table.Td><Text size="sm">{reasonLabel(r.returnReason) || "-"}</Text></Table.Td>
                              <Table.Td><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{dateFormat(r.requestedAt)}</Text></Table.Td>
                              <Table.Td ta="right"><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{currencyFormat(r.totalRequestedAmount ?? 0)}</Text></Table.Td>
                              <Table.Td><ReturnStatusBadge status={r.status} help /></Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </TableFrame>
                </DetailCard>
              )}
            </Stack>
          </Grid.Col>

          {/* ── Right: trigger context, recommended actions ── */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              <DetailCard title={t("prevention.detail.triggerContext")} helpKey={`${HELP}.section.trigger-context`}>
                <SideRow label={t("prevention.detail.pattern")} value={triggerLabel(alert.triggerType)} />
                {/* BE stores a percent (24.0 = 24%); no *100 (O146). */}
                <SideRow label={t("prevention.detail.metricValue")} value={percentText(alert.triggerMetricValue)} mono />
                <SideRow label={t("prevention.detail.thresholdValue")} value={percentText(alert.triggerThresholdValue)} mono />
                <SideRow label={t("prevention.detail.windowType")} value={alert.windowType} />
                <SideRow label={t("prevention.detail.productName")} value={alert.productName} />
                <SideRow label="SKU" value={alert.sku} mono />
              </DetailCard>

              {alert.recommendedActions.length > 0 && (
                <DetailCard title={t("prevention.detail.recommendedActions")} helpKey={`${HELP}.section.recommended`}>
                  <List type="ordered" spacing="xs" size="sm">
                    {alert.recommendedActions.map((action, idx) => (
                      <List.Item key={idx}>{action}</List.Item>
                    ))}
                  </List>
                </DetailCard>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      )}

      <AcknowledgeAlertModal
        opened={ackOpened}
        onClose={closeAck}
        alertId={id}
        onSuccess={mutate}
      />
      <MuteAlertModal
        opened={muteOpened}
        onClose={closeMute}
        alertId={id}
        onSuccess={mutate}
      />
      <ResolveAlertModal
        opened={resolveOpened}
        onClose={closeResolve}
        alertId={id}
        onSuccess={mutate}
      />
    </PageLayout>
  );
}
